/**
 * "Live" rail section: the run switcher (when more than one run is live) and
 * the selected run's card. Replaces the old `Select` dropdown labeled
 * `Run da3439d3` — a truncated execution id — with `LiveRunTabs` reading the
 * run's actual target.
 *
 * The tab strip sits with zero gap above the card (`attached` on
 * `LiveRunCard`) so the two read as one object, matching this page's other
 * lockup (filter bar/sparkline/table). Tab/panel wiring follows the ARIA APG
 * tabs pattern: every tab's `aria-controls` points at this one panel's `id`,
 * and the panel names itself via `aria-labelledby` on the *selected* tab.
 *
 * @module ui/pages/runs/test-runs/LiveRunsSection
 */
import * as React from 'react';
import { Text, type StatusIconBarItem } from '@chatsdet/ui';
import type { LiveRun } from '../../../../features/workflows/useLiveWorkflowTestExecutions';
import type { RunSummaryData } from '../../../../features/workflows/useRunSummaryData';
import type { ConnectionState } from '../../../../hooks/useWorkflowTestStream';
import { LiveRunCard } from './LiveRunCard';
import { LiveRunTabs, liveRunTabId } from './LiveRunTabs';

export type LiveRunsSectionProps = {
  runs: LiveRun[];
  /**
   * The run the card and (when there's more than one live run) the selected
   * tab both show — resolved once by the caller (`TestRunsPage`, which also
   * needs it to build `data`/`connectionState`/`onRetry`) and passed down
   * rather than re-derived here. Two independent copies of the same
   * `runs.find(...) ?? runs[0]` rule used to live in `TestRunsPage` and here;
   * if their ordering or fallback ever drifted apart, the card would render
   * one run's name over another run's counters. Typed non-null (not
   * `LiveRun | null`) so the impossible "no run to show" state can't be
   * represented here at all — the caller (`RunsRail`) only renders this
   * section once it has both a `selectedRun` and `data`.
   */
  selectedRun: LiveRun;
  onRunSelect: (id: string) => void;
  data: RunSummaryData;
  connectionState?: ConnectionState;
  onRetry?: () => void;
  onItemSelect?: (item: StatusIconBarItem) => void;
};

export function LiveRunsSection({
  runs,
  selectedRun,
  onRunSelect,
  data,
  connectionState,
  onRetry,
  onItemSelect,
}: LiveRunsSectionProps) {
  const panelId = React.useId();
  const hasTabs = runs.length > 1;

  return (
    <div>
      <Text size="xs" weight="semibold" className="mb-2 text-muted-foreground uppercase tracking-wide">
        Live
      </Text>
      {hasTabs ? (
        <>
          <LiveRunTabs
            runs={runs}
            selectedRunId={selectedRun.suiteId}
            onRunSelect={onRunSelect}
            panelId={panelId}
          />
          <div
            role="tabpanel"
            id={panelId}
            aria-labelledby={liveRunTabId(selectedRun.suiteId)}
            tabIndex={0}
            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-b-lg"
          >
            <LiveRunCard
              data={data}
              label={selectedRun.runTarget?.label ?? null}
              startedAt={selectedRun.startedAt}
              connectionState={connectionState}
              onRetry={onRetry}
              onItemSelect={onItemSelect}
              attached
            />
          </div>
        </>
      ) : (
        <LiveRunCard
          data={data}
          label={selectedRun.runTarget?.label ?? null}
          startedAt={selectedRun.startedAt}
          connectionState={connectionState}
          onRetry={onRetry}
          onItemSelect={onItemSelect}
        />
      )}
    </div>
  );
}
